import React, { useMemo, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronDown, Heart } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import { useWishlist } from '../context/WishlistContext';
import { getProducts, getProductsByCategory } from '../services/productService';
import { getCategories } from '../services/categoryService';

const Shop = () => {
  const { category } = useParams();
  const [sortBy, setSortBy] = useState('featured');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { formatMoney, getProductPrice, getProductCompareAtPrice } = useCurrency();
  const { wishlistIds, toggleWishlist } = useWishlist();

  const [filters, setFilters] = useState({
    inStockOnly: false,
    size: '',
    color: '',
    minPrice: '',
    maxPrice: '',
  });

  const [categories, setCategories] = useState(() => {
    try {
      return getCategories();
    } catch {
      return [];
    }
  });

  useEffect(() => {
    const refresh = () => {
      try {
        setCategories(getCategories());
      } catch {
        setCategories([]);
      }
    };
    refresh();
    window.addEventListener('ditris:categories', refresh);
    return () => window.removeEventListener('ditris:categories', refresh);
  }, []);

  useEffect(() => {
    const loadProducts = async () => {
      setIsLoading(true);
      try {
        let loadedProducts;
        if (category && category !== 'new-arrivals') {
          loadedProducts = await getProductsByCategory(category);
        } else {
          loadedProducts = await getProducts();
        }
        setProducts(loadedProducts);
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
  }, [category]);

  useEffect(() => {
    setFilters({
      inStockOnly: false,
      size: '',
      color: '',
      minPrice: '',
      maxPrice: '',
    });
    setShowSortDropdown(false);
  }, [category]);

  const allSizes = useMemo(() => {
    const set = new Set();
    (products || []).forEach((p) => (p.sizes || []).forEach((s) => set.add(String(s))));
    return Array.from(set.values()).sort((a, b) => a.localeCompare(b));
  }, [products]);

  const allColors = useMemo(() => {
    const set = new Set();
    (products || []).forEach((p) => (p.colors || []).forEach((c) => set.add(String(c))));
    return Array.from(set.values()).sort((a, b) => a.localeCompare(b));
  }, [products]);

  const filteredProducts = useMemo(() => {
    const min = filters.minPrice === '' ? null : Number(filters.minPrice);
    const max = filters.maxPrice === '' ? null : Number(filters.maxPrice);

    return (products || []).filter((p) => {
      if (filters.inStockOnly && !p.inStock) return false;
      if (filters.size && !(p.sizes || []).map(String).includes(String(filters.size))) return false;
      if (filters.color && !(p.colors || []).map(String).includes(String(filters.color))) return false;
      const price = getProductPrice(p);
      if (min !== null && Number.isFinite(min)) {
        if (price === null) return false;
        if (Number(price) < min) return false;
      }
      if (max !== null && Number.isFinite(max)) {
        if (price === null) return false;
        if (Number(price) > max) return false;
      }
      return true;
    });
  }, [products, filters, getProductPrice]);

  // Sort products
  const sortedProducts = useMemo(() => {
    const arr = [...(filteredProducts || [])];
    arr.sort((a, b) => {
      switch (sortBy) {
        case 'newest': {
          const ad = Date.parse(a.updatedAt || a.createdAt || 0);
          const bd = Date.parse(b.updatedAt || b.createdAt || 0);
          return (Number.isFinite(bd) ? bd : 0) - (Number.isFinite(ad) ? ad : 0);
        }
        case 'price-low': {
          const ap = getProductPrice(a);
          const bp = getProductPrice(b);
          const av = ap === null ? Number.POSITIVE_INFINITY : Number(ap);
          const bv = bp === null ? Number.POSITIVE_INFINITY : Number(bp);
          return av - bv;
        }
        case 'price-high': {
          const ap = getProductPrice(a);
          const bp = getProductPrice(b);
          const av = ap === null ? Number.NEGATIVE_INFINITY : Number(ap);
          const bv = bp === null ? Number.NEGATIVE_INFINITY : Number(bp);
          return bv - av;
        }
        case 'name':
          return String(a.name || '').localeCompare(String(b.name || ''));
        default:
          return 0;
      }
    });
    return arr;
  }, [filteredProducts, sortBy, getProductPrice]);

  const categoryName = categories.find((c) => c.slug === category)?.name || 'ALL PRODUCTS';

  const hasActiveFilters =
    !!filters.inStockOnly ||
    !!filters.size ||
    !!filters.color ||
    filters.minPrice !== '' ||
    filters.maxPrice !== '';

  const clearFilters = () => {
    setFilters({
      inStockOnly: false,
      size: '',
      color: '',
      minPrice: '',
      maxPrice: '',
    });
  };

  const onToggleWishlist = async (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await toggleWishlist(productId);
    } catch {
      // ignore
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-16 text-center">
        <p className="text-gray-500">Loading products...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-medium tracking-wide mb-2">{categoryName}</h1>
          <p className="text-sm text-gray-600">{sortedProducts.length} products</p>
        </div>

        {/* Sort Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowSortDropdown(!showSortDropdown)}
            className="flex items-center gap-2 border border-gray-300 px-4 py-2 text-sm hover:border-black transition-colors"
          >
            Sort by: {sortBy === 'featured' ? 'Featured' : sortBy === 'newest' ? 'Newest' : sortBy === 'price-low' ? 'Price: Low to High' : sortBy === 'price-high' ? 'Price: High to Low' : 'Name'}
            <ChevronDown size={16} />
          </button>
          {showSortDropdown && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 shadow-lg min-w-[200px] z-10">
              <button
                onClick={() => { setSortBy('featured'); setShowSortDropdown(false); }}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors"
              >
                Featured
              </button>
              <button
                onClick={() => { setSortBy('newest'); setShowSortDropdown(false); }}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors"
              >
                Newest
              </button>
              <button
                onClick={() => { setSortBy('price-low'); setShowSortDropdown(false); }}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors"
              >
                Price: Low to High
              </button>

              <button
                onClick={() => { setSortBy('price-high'); setShowSortDropdown(false); }}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors"
              >
                Price: High to Low
              </button>
              <button
                onClick={() => { setSortBy('name'); setShowSortDropdown(false); }}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors"
              >
                Name
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="border border-gray-200 p-4 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-1">
            <label className="block text-xs tracking-widest text-gray-600 mb-2">SIZE</label>
            <select
              value={filters.size}
              onChange={(e) => setFilters((p) => ({ ...p, size: e.target.value }))}
              className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-black"
            >
              <option value="">All</option>
              {allSizes.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-1">
            <label className="block text-xs tracking-widest text-gray-600 mb-2">COLOR</label>
            <select
              value={filters.color}
              onChange={(e) => setFilters((p) => ({ ...p, color: e.target.value }))}
              className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-black"
            >
              <option value="">All</option>
              {allColors.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-1">
            <label className="block text-xs tracking-widest text-gray-600 mb-2">MIN PRICE</label>
            <input
              value={filters.minPrice}
              onChange={(e) => setFilters((p) => ({ ...p, minPrice: e.target.value }))}
              type="number"
              min="0"
              className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-black"
              placeholder="0"
            />
          </div>

          <div className="md:col-span-1">
            <label className="block text-xs tracking-widest text-gray-600 mb-2">MAX PRICE</label>
            <input
              value={filters.maxPrice}
              onChange={(e) => setFilters((p) => ({ ...p, maxPrice: e.target.value }))}
              type="number"
              min="0"
              className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-black"
              placeholder="500"
            />
          </div>

          <div className="md:col-span-1 flex items-end justify-between gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={filters.inStockOnly}
                onChange={(e) => setFilters((p) => ({ ...p, inStockOnly: e.target.checked }))}
              />
              In stock only
            </label>
            {hasActiveFilters ? (
              <button onClick={clearFilters} className="text-sm underline hover:opacity-60">
                Clear
              </button>
            ) : (
              <div />
            )}
          </div>
        </div>
      </div>

      {/* Products Grid */}
      {sortedProducts.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-gray-500">No products match your filters.</p>
          {hasActiveFilters ? (
            <button onClick={clearFilters} className="mt-4 text-sm underline hover:opacity-60">
              Clear filters
            </button>
          ) : null}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {sortedProducts.map((product) => (
            <Link
              key={product.id}
              to={`/product/${product.id}`}
              className="group"
            >
              <div className="aspect-square bg-gray-100 mb-3 overflow-hidden relative">
                <img
                  src={(() => {
                    const imgs = Array.isArray(product.images) ? product.images : [];
                    const idx = Number(product.primaryImageIndex ?? 0);
                    return imgs[(Number.isFinite(idx) && idx >= 0 ? idx : 0)] || imgs[0];
                  })()}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <button
                  onClick={(e) => onToggleWishlist(e, product.id)}
                  className={`absolute top-2 right-2 w-9 h-9 flex items-center justify-center border transition-colors ${
                    wishlistIds.includes(String(product.id))
                      ? 'border-black bg-black text-white'
                      : 'border-gray-200 bg-white/90 hover:bg-white hover:border-black'
                  }`}
                  aria-label="Toggle wishlist"
                >
                  <Heart size={16} />
                </button>
                {!product.inStock && (
                  <div className="absolute top-2 left-2 bg-black text-white text-xs px-2 py-1">
                    SOLD OUT
                  </div>
                )}
              </div>
              <h3 className="text-sm font-medium mb-1">{product.name}</h3>
              {(() => {
                const price = getProductPrice(product);
                const mrp = getProductCompareAtPrice(product);
                if (price !== null && mrp !== null && Number(mrp) > Number(price)) {
                  return (
                    <div className="text-sm">
                      <span className="text-gray-500 line-through mr-2">{formatMoney(mrp)}</span>
                      <span>{formatMoney(price)}</span>
                    </div>
                  );
                }
                return <p className="text-sm">{formatMoney(price)}</p>;
              })()}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Shop;